import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
    Warranty,
    WarrantyHistory,
    WarrantyInput,
    WarrantyServiceEntry,
} from '@econtractor/types';
import * as api from 'api/apiWarranties';
import { useToast } from 'context/ToastContext';
import { useNeedPay } from 'modules/admin';
import { todayIsoDate } from './warrantyDates';

const useWarrantyCard = (initialId: number, onSaved: () => void, onClose: () => void = () => undefined) => {
    const { t } = useTranslation('Warranties');
    const { showToast } = useToast();
    const { needPay } = useNeedPay();
    const [id, setId] = useState(initialId);
    const [row, setRow] = useState<Warranty | null>(null);
    const [history, setHistory] = useState<WarrantyHistory[]>([]);
    const [service, setService] = useState<WarrantyServiceEntry[]>([]);
    const [loading, setLoading] = useState(Boolean(initialId));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setLoading(true);
        void Promise.all([api.read(id), api.history(id), api.service(id)])
            .then(([record, events, entries]) => {
                if (cancelled) return;
                setRow(record);
                setHistory(events);
                setService(entries);
                setError('');
            })
            .catch((error) => {
                if (!cancelled) setError(t(`errors.${api.errorCode(error)}`));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, revision, t]);
    const report = useCallback(
        (error: unknown) => {
            showToast(t(`errors.${api.errorCode(error)}`), 'danger');
        },
        [showToast, t],
    );
    const save = async (values: WarrantyInput & { serialNumbers?: string[] }): Promise<number | false> => {
        if (!id && needPay()) return false;
        setBusy(true);
        try {
            if (!id) {
                const serialNumbers = (values.serialNumbers || [])
                    .map((serial) => serial.trim())
                    .filter(Boolean);
                const keys = serialNumbers.map((serial) => serial.toLowerCase());
                if (!serialNumbers.length) return false;
                if (new Set(keys).size !== keys.length) {
                    showToast(t('errors.DUPLICATE'), 'danger');
                    return false;
                }
                const result = await api.create({
                    productId: values.productId,
                    serialNumbers,
                    purchasedAt: values.purchasedAt,
                    warrantyUntil: values.warrantyUntil,
                    actualVendorId: values.actualVendorId ?? null,
                });
                if (!result.stat) throw new Error('save');
                onSaved();
                onClose();
                return result.ID || false;
            }
            const result = await api.save(id, {
                productId: values.productId,
                serialNumber: values.serialNumber,
                purchasedAt: values.purchasedAt,
                warrantyUntil: values.warrantyUntil,
                objectId: values.objectId,
                actualVendorId: values.actualVendorId ?? null,
            });
            if (!result.stat || !result.ID) throw new Error('save');
            setId(result.ID);
            setRevision((r) => r + 1);
            onSaved();
            return result.ID;
        } catch (error) {
            report(error);
            return false;
        } finally {
            setBusy(false);
        }
    };
    const archive = async () => {
        setBusy(true);
        try {
            await api.archive(id, !row?.archived);
            setRevision((r) => r + 1);
            onSaved();
        } catch (error) {
            report(error);
        } finally {
            setBusy(false);
        }
    };
    const addService = async (text: string, serviceDate = todayIsoDate()): Promise<boolean> => {
        setBusy(true);
        try {
            await api.addService(id, text, serviceDate);
            setService(await api.service(id));
            return true;
        } catch (error) {
            report(error);
            return false;
        } finally {
            setBusy(false);
        }
    };
    const saveFiles = async (files: string, savedId = id): Promise<boolean> => {
        try {
            await api.files(savedId, files);
            setRow((prev) => (prev ? { ...prev, files } : prev));
            return true;
        } catch (error) {
            report(error);
            return false;
        }
    };
    return {
        id,
        row,
        history,
        service,
        loading,
        busy,
        error,
        save,
        archive,
        addService,
        saveFiles,
    };
};

export default useWarrantyCard;
